import { identity, waHref } from "@/content/identity";
import { siteUrl } from "@/lib/metadata";
import type { CaseItem } from "@/lib/contentful-types";

const cleanPhone = (phone: string) => phone.replace(/[^\d+]/g, "");

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": `${siteUrl}/#business`,
  name: identity.nombre,
  description:
    "Codiaj diseña webs rápidas, SEO local y automatizaciones para restaurantes, pymes y empresas corporativas en Madrid y España.",
  url: siteUrl,
  logo: `${siteUrl}/brand/logo-full.png`,
  image: `${siteUrl}/brand/logo-full.png`,
  telephone: identity.telefonos.map(cleanPhone),
  email: identity.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: "C/ Pablo Gargalo 35",
    addressLocality: "Madrid",
    postalCode: "28035",
    addressCountry: "ES",
  },
  areaServed: [
    { "@type": "City", name: "Madrid" },
    { "@type": "AdministrativeArea", name: "Comunidad de Madrid" },
    { "@type": "Country", name: "España" },
  ],
  priceRange: "€€",
  currenciesAccepted: "EUR",
  paymentAccepted: "Pago tras satisfacción",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "19:00",
  },
  knowsAbout: [
    "diseño web para restaurantes",
    "SEO local para restaurantes",
    "páginas web corporativas",
    "sistemas de reservas",
    "carta digital",
    "automatización para pymes",
  ],
  makesOffer: [
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "Diseño web para restaurantes",
        serviceType: "Web design and local SEO for restaurants",
        areaServed: "Madrid",
        audience: {
          "@type": "BusinessAudience",
          audienceType: "Restaurantes, bares, cafeterías y negocios hosteleros",
        },
        description:
          "Webs para restaurantes con SEO local, WhatsApp, carta digital, reservas, Google Maps y captación de clientes cercanos.",
      },
    },
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "Diseño web corporativo",
        serviceType: "Corporate website design",
        areaServed: "España",
        audience: {
          "@type": "BusinessAudience",
          audienceType: "Pymes, autónomos y empresas corporativas",
        },
        description:
          "Webs corporativas rápidas, claras y optimizadas para SEO técnico, confianza de marca y generación de oportunidades.",
      },
    },
  ],
  sameAs: [
    waHref(identity.telefonos[0]),
    "https://instagram.com/codiaj",
    "https://tiktok.com/@codiaj",
    "https://youtube.com/@codiaj",
  ],
};

export const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${siteUrl}/#website`,
  url: siteUrl,
  name: identity.nombre,
  description:
    "Diseño web profesional para restaurantes, pymes y empresas en Madrid sin pago inicial.",
  publisher: { "@id": `${siteUrl}/#business` },
  inLanguage: "es-ES",
  potentialAction: {
    "@type": "SearchAction",
    target: `${siteUrl}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const restaurantAndCorporateServicesSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${siteUrl}/servicios#sectores-principales`,
  name: "Servicios web para restaurantes y empresas corporativas",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@type": "Service",
        name: "Web para restaurantes con SEO local",
        provider: { "@id": `${siteUrl}/#business` },
        areaServed: "Madrid",
        serviceType: "Diseño web, SEO local, carta digital, reservas y WhatsApp",
        audience: {
          "@type": "BusinessAudience",
          audienceType: "Restaurantes y hostelería",
        },
      },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@type": "Service",
        name: "Web corporativa para empresas",
        provider: { "@id": `${siteUrl}/#business` },
        areaServed: "España",
        serviceType: "Diseño web corporativo, SEO técnico y captación B2B",
        audience: {
          "@type": "BusinessAudience",
          audienceType: "Pymes y empresas corporativas",
        },
      },
    },
  ],
};

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export function caseStudySchema(item: CaseItem) {
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${siteUrl}/casos-exito/${item.slug}#case-study`,
    name: `${item.client} — caso de éxito de diseño web`,
    headline: `${item.client} — caso de éxito`,
    description: item.resumen?.slice(0, 300),
    url: `${siteUrl}/casos-exito/${item.slug}`,
    image: item.imagenPrincipal?.url,
    creator: { "@id": `${siteUrl}/#business` },
    about: item.tipo,
    keywords: [
      item.tipo,
      item.tag,
      ...item.stack,
      "diseño web",
      "SEO local",
      item.tipo.toLowerCase().includes("restaurante")
        ? "web para restaurantes"
        : "web corporativa",
    ].filter(Boolean),
  };
}
